/**
 * =====================================================
 * AVA CLIENT - Portal de Solicitantes
 * Puerto: 3000
 * =====================================================
 * 
 * Mejoras de performance (qa_mvp_manus):
 * - React.lazy + Suspense para code splitting
 * - Solo Login y Home se cargan en el bundle inicial
 * - Las demás páginas se cargan bajo demanda
 */

import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useScrollToTop } from "@/hooks/useScrollToTop";

// =====================================================
// Code Splitting: páginas cargadas bajo demanda
// Solo Home y Login van en el bundle inicial (critical path)
// =====================================================
import Home from "./pages/Home";
import Login from "./pages/Login";

// Lazy-loaded pages (se cargan cuando el usuario navega)
const IdentityVerification = lazy(() => import("./pages/IdentityVerification").then(m => ({ default: m.IdentityVerification })));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Documents = lazy(() => import("./pages/Documents"));
const ReviewData = lazy(() => import("./pages/ReviewData"));
const Beneficiaries = lazy(() => import("./pages/Beneficiaries"));
const Apoderados = lazy(() => import("./pages/Apoderados"));
const Result = lazy(() => import("./pages/Result"));
const SignerIdentityVerification = lazy(() => import("./pages/SignerIdentityVerification").then(m => ({ default: m.SignerIdentityVerification })));
const PersonsIdentityVerification = lazy(() => import("./pages/PersonsIdentityVerification").then(m => ({ default: m.PersonsIdentityVerification })));

/**
 * Componente de loading para Suspense
 * Muestra un skeleton sutil mientras se carga la página
 */
function PageLoadingFallback() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground">Cargando...</p>
      </div>
    </div>
  );
}

/**
 * TALAPP-1841 — reset de scroll en cada cambio de ruta.
 *
 * Va acá, hermano del <Switch> y no dentro de cada pantalla: wouter no
 * resetea el scroll en la navegación SPA, así que TODAS las rutas heredaban
 * el offset de la anterior (se veía en /review, la más alta del wizard).
 * Un solo punto de verdad — una pantalla nueva queda cubierta sola.
 *
 * No renderiza nada ni envuelve al <Switch>: no agrega reconciliación al
 * árbol de páginas.
 */
function ScrollToTop() {
  useScrollToTop();
  return null;
}

function Router() {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoadingFallback />}>
        <Switch>
          {/* Rutas públicas */}
          <Route path={"/"} component={Home} />
          <Route path={"/login"} component={Login} />
          <Route path={"/register"} component={Register} />
          <Route path={"/forgot-password"} component={ForgotPassword} />

          {/* Rutas protegidas: requieren autenticación */}
          <Route path={"/identity-verification"}>
            {() => <ProtectedRoute component={IdentityVerification} />}
          </Route>
          <Route path={"/onboarding/:requestId/documents"}>
            {(params) => <ProtectedRoute component={Documents} {...params} />}
          </Route>
          <Route path={"/onboarding/:requestId/review"}>
            {(params) => <ProtectedRoute component={ReviewData} {...params} />}
          </Route>
          <Route path={"/onboarding/:requestId/beneficiaries"}>
            {(params) => <ProtectedRoute component={Beneficiaries} {...params} />}
          </Route>
          <Route path={"/onboarding/:requestId/apoderados"}>
            {(params) => <ProtectedRoute component={Apoderados} {...params} />}
          </Route>
          <Route path={"/onboarding/:requestId/identity"}>
            {(params) => <ProtectedRoute component={SignerIdentityVerification} {...params} />}
          </Route>
          <Route path={"/onboarding/:requestId/verification"}>
            {(params) => <ProtectedRoute component={PersonsIdentityVerification} {...params} />}
          </Route>
          <Route path={"/onboarding/:requestId/analyze"}>
            {(params) => <ProtectedRoute component={Result} {...params} />}
          </Route>

          <Route path={"/404"} component={NotFound} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          {/* AVA DS v4 toasts.
              Look: fondo blanco + stripe top 3px del tono + icono en pill
              + close arriba-derecha + action con underline.
              Implementado via classNames mapeados a clases ava-toast* + CSS
              en index.css que usa [data-type=...] de Sonner para el color.
              NO usamos richColors (eso pinta el fondo entero, choca con el DS).
              Mobile: media query en index.css lo lleva a bottom con safe-area. */}
          <Toaster
            position="top-center"
            closeButton
            expand
            visibleToasts={5}
            offset={80}
            toastOptions={{
              duration: 5000,
              classNames: {
                toast: "ava-toast",
                title: "ava-toast-title",
                description: "ava-toast-description",
                actionButton: "ava-toast-action",
                cancelButton: "ava-toast-cancel",
                closeButton: "ava-toast-close",
              },
            }}
          />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
